import type { EntityType, ListQuery } from '../api';

/** Built-in presets of each list ("my", "in work", "won this month"…). */
export type PresetId =
  | 'all'
  | 'my'
  | 'inWork'
  | 'wonThisMonth'
  | 'lost'
  | 'converted'
  | 'junk'
  | 'withoutActivities'
  | 'accepted'
  | 'unpaid'
  | 'overdue'
  | 'paidThisMonth';

export const PRESETS: Record<EntityType, PresetId[]> = {
  deal: ['all', 'my', 'inWork', 'withoutActivities', 'wonThisMonth', 'lost'],
  lead: ['all', 'my', 'inWork', 'withoutActivities', 'converted', 'junk'],
  contact: ['all', 'my'],
  company: ['all', 'my'],
  quote: ['all', 'my', 'inWork', 'accepted'],
  invoice: ['all', 'my', 'unpaid', 'overdue', 'paidThisMonth'],
  item: ['all', 'my', 'inWork', 'withoutActivities', 'lost'],
};

/** Filter keys each list understands (also what a saved preset stores). */
export const FILTER_KEYS: Record<EntityType, string[]> = {
  deal: [
    'search',
    'semantic',
    'stageId',
    'responsibleId',
    'companyId',
    'sourceId',
    'activities',
    'createdFrom',
    'createdTo',
    'closedFrom',
    'closedTo',
    'sort',
  ],
  lead: [
    'search',
    'semantic',
    'statusId',
    'responsibleId',
    'sourceId',
    'activities',
    'createdFrom',
    'createdTo',
    'sort',
  ],
  contact: ['search', 'type', 'responsibleId', 'companyId', 'sourceId', 'createdFrom', 'createdTo', 'sort'],
  company: ['search', 'type', 'industry', 'responsibleId', 'createdFrom', 'createdTo', 'sort'],
  quote: ['search', 'status', 'responsibleId', 'companyId', 'dealId', 'createdFrom', 'createdTo', 'sort'],
  invoice: [
    'search',
    'status',
    'responsibleId',
    'companyId',
    'dealId',
    'createdFrom',
    'createdTo',
    'paidFrom',
    'paidTo',
    'sort',
  ],
  item: [
    'search',
    'semantic',
    'stageId',
    'responsibleId',
    'companyId',
    'activities',
    'createdFrom',
    'createdTo',
    'closedFrom',
    'closedTo',
    'sort',
  ],
};

/** URL keys of the custom field filters of a field: `cf.<code>` or the range `cf.<code>.from` / `.to`. */
export function customFilterKeys(field: { code: string; type: string }): string[] {
  return field.type === 'number' || field.type === 'money' || field.type === 'date'
    ? [`cf.${field.code}.from`, `cf.${field.code}.to`]
    : [`cf.${field.code}`];
}

const startOfMonth = (now: Date) => new Date(now.getFullYear(), now.getMonth(), 1);

/** The filters a built-in preset stands for. */
export function presetQuery(
  type: EntityType,
  preset: PresetId,
  me: string | undefined,
  now = new Date(),
): ListQuery {
  switch (preset) {
    case 'my':
      return { responsibleId: me };
    case 'inWork':
      return type === 'quote' ? { status: 'draft,sent' } : { semantic: 'open' };
    case 'accepted':
      return { status: 'accepted' };
    case 'unpaid':
      return { status: 'issued,overdue' };
    case 'overdue':
      return { status: 'overdue' };
    case 'paidThisMonth':
      return { status: 'paid', paidFrom: startOfMonth(now).toISOString() };
    case 'wonThisMonth':
      return type === 'deal' ? { semantic: 'won', closedFrom: startOfMonth(now).toISOString() } : {};
    case 'lost':
      return { semantic: 'lost' };
    case 'converted':
      return { semantic: 'converted' };
    case 'junk':
      return { semantic: 'junk' };
    case 'withoutActivities':
      // Records in work without a next step planned (Bitrix-style daily control).
      return { semantic: 'open', activities: 'none' };
    default:
      return {};
  }
}

/** Counts the filters set besides search, sort and the preset's own. */
export function activeFilters(query: Record<string, string | undefined>, ignore: string[] = []): number {
  return Object.entries(query).filter(
    ([key, value]) =>
      value !== undefined && value !== '' && !['search', 'sort', 'page', 'preset', ...ignore].includes(key),
  ).length;
}
